import { Crop } from '@next/arable-lib/crop';
import { WurSoilTypes } from '@next/arable-lib/shared';
import { RootState } from '@next/core-lib/app';
import { SessionState } from '@next/core-lib/session';
import { EntityState } from '@ngrx/entity';
import * as jsts from 'jsts';
import GeoJSON from 'ol/format/GeoJSON';

import { Feature, FeatureState, MapContextState } from '../geo/models';
import { ReportSort } from '../reports/models';
import { SoilAnalysisState } from '../soil-analysis/models/soil-analysis.model';

// add your interfaces for the api responses and state here
// AppState is the root state of your app module
export interface SiteState extends RootState {
  'crop-field'?: CropFieldState;
  crop?: CropState;
  'crop-varieties-by-crop-id'?: CropVarietiesByCropIdState;
  'soil-type'?: SoilTypeState;
  'map-context'?: MapContextState;
  feature?: FeatureState;
  soilAnalysis?: SoilAnalysisState;
  app?: AppState;
}

export enum CropFieldType {
  Advice = 'Advice',
  ManureSpace = 'ManureSpace',
}

export interface ArableSessionState extends SessionState {
  arable?: {
    cropFieldType?: CropFieldType;
    cropYear?: number;
  };
}

export interface AppState {
  defaultCropFieldType?: CropFieldType;
  defaultCropYear?: number;
  availableCropYears?: number[];
  cropYears?: number[];
  search?: boolean;
  reportSort?: ReportSort;
  customerProgress?: CustomerProgress;
  availableTgaCrops?: number[];
}

export interface CropFieldState extends EntityState<CropField> {
  entitiesLastYear?: CropField[];
  filteredIds?: number[];
  isPrefilling?: boolean;
  agroConnectCustomers?: AgroConnectCustomer[];
  isAwaitingSync?: boolean;
  syncId?: Date;
}

export interface CropState extends EntityState<Crop> {}

export interface CropVarietiesByCropIdState extends EntityState<CropVarietiesByCropId> {}

export interface SoilTypeState extends EntityState<SoilType> {}

export interface TgaCropState extends EntityState<number[]> {}

export interface LayerData {
  soilTypeFertilizerLaw?: string;
  sandySoilLaw?: WurSoilTypes;
  isGroundWaterPreservation?: boolean;
  isNutrientsPollutedArea?: boolean;
}

export interface CropFieldProperties {
  cropId: number;
  cropFieldType: CropFieldType;
  cropVarietyId: number;
  conceptId?: string | null;
  chainPartnerId?: string | null;
  soilTypeId: number;
  name: string;
  areaInHectares: number;
  isSkal: boolean;
  isPlanetProof: boolean;
  isGroundWaterPreservation: boolean;
  isSandySoil: boolean;
  isRentedField: boolean;
  startDate: string;
  endDate: string;
  layerData?: LayerData;
  soilAnalysisIds?: number[];
  province?: Province;
  machiningDepthInCentimeters: number;
  isSubsequentCultivation: boolean;
  outsideDutchBorders: boolean;
  originalCropFieldIdForSubsequentCultivation?: number;
  isNutrientsPollutedArea: boolean;
  isGroundWaterPreservationWithNitrogenCuts: boolean;
}

export interface CropField extends CropFieldProperties {
  id: number;
  feature?: Feature;
  isAreaCalculated: boolean;
  soilAnalysisIds: number[];
  originalCropFieldId?: number;
  externalId?: string;
  externalIdSource?: string;
  syncStatus?: SyncStatusEnum;
  palValueFrom2021?: number;
  pcacl2Value?: number;
  isParticipatingToBem?: boolean;
  isParticipatingToTga?: boolean;
  isCancellingBem?: boolean;
  plantDate?: Date;
  isPlantDateConfirmed?: boolean;
}

export interface DetailedCropField extends CropField {
  cropName: string;
  isParticipatingToBem?: boolean;
  isParticipatingToTga?: boolean;
}

export interface GroupedCropFields {
  mainCultivations: GroupedCropField[];
  mainCultivationsTotalFields: number;
  mainCultivationsTotalHa: number;
  subsequentCultivations: GroupedCropField[];
  subsequentCultivationsTotalHa: number;
  subsequentCultivationsTotalFields: number;
}

export interface GroupedCropField {
  cropName: string;
  totalHectares: number;
  cropFields: DetailedCropField[];
}

export interface MostUsedCropReference {
  id: number;
  count: number;
}

export interface CropVariety {
  id: number;
  name: string;
  cropId: number;
  isDeprecated: boolean;
}

export interface CropVarietiesByCropId {
  id: number;
  cropVarieties: CropVariety[];
}

export interface MonthDay {
  month: number;
  day: number;
}

export interface SoilType {
  id: number;
  name: string;
}

export interface GetDefaultYearResponse {
  defaultYear: number;
}

export interface GetCropFieldsResponse {
  cropFields: CropField[];
}

export interface UpsertCropFieldResponse {
  id: number;
}
export interface UpsertCropFieldsWithRegulatoryParcelResponse {
  succeeded: UpsertCropFieldResponse[];
  failed: FailedUpsert[];
}

export interface FailedUpsert {
  name: string;
  areaInHectares: number;
  startDate: string;
  endDate: string;
  error: string;
}

export interface GetCropsResponse {
  crops: Crop[];
}

export interface GetMostUsedCropsResponse {
  mostUsedCrops: MostUsedCropReference[];
}

export interface GetCropVarietiesResponse {
  cropVarieties: CropVariety[];
}

export interface EditCropFieldsOptions {
  showAddAnotherButton?: boolean;
  isSubsequentCultivation?: boolean;
}

export interface CreateCropFieldsOptions {
  addAnotherField?: boolean;
  nextCropFeatures?: Feature[];
  originalCropFieldsToDelete?: CropField[];
  linkSoilAnalysisOrigin?: boolean;
  closeRvoLayer?: boolean;
}

export interface CombineGeometryPrepareResult {
  success: boolean;
  isIntersecting: boolean;
  hasCorrectNumberOfFieldFeatures: boolean;
  parser: jsts.io.OL3Parser;
  format: GeoJSON;
  field1Geom: jsts.geom.Geometry;
  field2Geom: jsts.geom.Geometry;
}

export interface DivideGeometryResult {
  validation: DivideValidation;
  parser: jsts.io.OL3Parser;
  format: GeoJSON;
  field1Geom: jsts.geom.Geometry;
  field2Geom: jsts.geom.Geometry;
}

export interface ShapefileResponse {
  features: Feature[];
}

export enum DivideValidation {
  success,
  invalidSelection,
  notIntersecting,
  notProducingTwoFields,
  wrongNumberOfGeometries,
}

export enum Province {
  Drenthe = 'Drenthe',
  Flevoland = 'Flevoland',
  Friesland = 'Friesland',
  Gelderland = 'Gelderland',
  Groningen = 'Groningen',
  Limburg = 'Limburg',
  NoordBrabant = 'Noord-Brabant',
  NoordHolland = 'Noord-Holland',
  Overijssel = 'Overijssel',
  Utrecht = 'Utrecht',
  Zeeland = 'Zeeland',
  ZuidHolland = 'Zuid-Holland',
}

/**
 * Note: In the back-end this isn't an enum but a database type.
 * We can only match it on the name because id's might differ per environment.
 */
export enum SoilTypes {
  Zand = 'Zand',
  Klei = 'Klei',
  Loss = 'Loss',
  Veen = 'Veen',
}

export enum SyncStatusEnum {
  New = 'New',
  Updated = 'Updated',
  NotPresent = 'NotPresent',
}

export enum CopyCropFieldToManureSpaceEvents {
  CopyCropFieldsToManureSpaceStarted = 'Arable_CopyCropFieldsToManureSpaceStarted',
  CopyCropFieldsToManureSpaceCompleted = 'Arable_CopyCropFieldsToManureSpaceCompleted',
}

export interface CopyCropFieldToManureSpaceState {
  amountOfCropFieldsCopying?: number;
  amountOfCropFieldsCompleted?: number;
  copyDialogDismissed?: boolean;
  copyStatusDismissed?: boolean;
}

export interface CopyCropFieldsToManureSpaceStartedEvent {
  AmountOfCropFields: number;
}

export interface CopyCropFieldsToManureSpaceCompletedEvent {
  isSuccessful: boolean;
  message?: string;
}

export interface CopyCropFieldsToManureSpaceResponse {
  amountOfCropFields: number;
}

export interface MultiSelectItem {
  name: string;
  value: number;
  description?: string;
}

export interface CropFieldsImportProvider {
  agroConnect: AgroConnectExternalParty;
  rvo: RvoProvider;
}

export enum AgroConnectExternalParty {
  Dacom = 'Dacom',
  CropVision = 'CropVision',
}

export enum RvoProvider {
  RvO = 'RvO',
  RvORegulatoryParcelManure = 'RvORegulatoryParcelManure',
}

export interface AgroConnectCustomer {
  farmId: string;
  farmDesignator: string;
  postCode: string;
  streetName: string;
  cityName: string;
}

export interface AgroConnectCropField {
  id?: number;
  thirdPartyId: string;
  name: string;
  beginDate: string;
  endDate: string;
  country: string;
  hectares: number;
  soilTypeId: number;
  cropId: number;
  cropVarietyId: number;
  coordinates: number[][];
  palValueFrom2021?: number;
  pcacl2Value?: number;
  isSubsequentCultivation?: boolean;
}

export interface AgroConnectCropFieldWithDifferences {
  id: number;
  thirdPartyId: string;
  name: { oldValue: string; newValue: string };
  beginDate: { oldValue: string; newValue: string };
  endDate: { oldValue: string; newValue: string };
  country: { oldValue: string; newValue: string };
  hectares: { oldValue: number; newValue: number };
  soilTypeId: { oldValue: number; newValue: number };
  cropId: { oldValue: number; newValue: number };
  cropVarietyId: { oldValue: number; newValue: number };
  coordinates: number[][];
}

export interface ChangedCropFields {
  newValues: CropField[];
  oldValues: CropField[];
}

export interface CropFieldsSyncResponse {
  changedCropFields: AgroConnectCropFieldWithDifferences[];
  deletedCropFields: AgroConnectCropField[];
  newCropFields: AgroConnectCropField[];
  externalCropFieldsCount: number;
  ediErrorCode?: RvOErrorCode;
}

export interface RetrieveCropfieldsSource {
  provider: AgroConnectExternalParty;
  year: number;
  farmId: string;
}

export interface ImportCropfieldsSelection {
  cropFields: CropField[];
  year: number;
}

export interface CustomerProgress {
  id: number;
  customerId: number;
  year: number;
  processes: CustomerProgressProcesses;
}

export interface CustomerProgressProcesses {
  fieldPlanCropFieldsCompleted: boolean;
  fieldPlanManurespaceCompleted: boolean;
  fieldAdviceFertilizationAdvicesCompleted: boolean;
  fieldAdviceCropProtectionAdvicesCompleted: boolean;
  orderplanningOrderCompleted: boolean;
  manurespaceCalculationsCompleted: boolean;
}

export const customerProgressForSelectionUrl =
  '/arable-service/organisation/customerprogress/customers';

export interface GetTgaCropIdsResponse {
  ids: number[];
}

export enum BlightSyncStatus {
  syncing = 'Syncing',
  synced = 'Synced',
  mock = 'mock',
}

export enum RvOErrorCode {
  EDI001 = 'RELATION_NUMBER_NOT_FOUND',
  EDI009 = 'NO_DISTRIBUTION_MANDATE',
}

export interface MvcValidationErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  traceId?: string;
  errors?: Record<string, string[]>;
}

// V2 Crop Field Synchronization Interfaces

export enum ExternalCropFieldSources {
  Dacom = 'Dacom',
  CropVision = 'CropVision',
  RvO = 'RvO',
  RvORegulatoryParcelManure = 'RvORegulatoryParcelManure',
}

export interface TriggerSyncCropFieldsRequest {
  searchYear: number;
  cropFieldType: CropFieldType;
  externalCropFieldSource: ExternalCropFieldSources;
  externalId: string;
}

export interface SyncCropFieldsReady {
  customerId: number;
  searchYear: number;
  externalId: string;
  succeeded: boolean;
  errorCode?: string[];
}

export interface ExternalCropFieldDto {
  externalId: string;
  name: string;
  beginDate: string;
  endDate: string;
  hectares: number;
  cropId: number;
  cropVarietyId?: number;
  isSubsequentCultivation: boolean;
}

export interface ExistingCropFieldDto {
  cropFieldId: number;
  name: string;
  hectares: number;
  cropId: number;
  cropVarietyId?: number;
}

export interface CropFieldSyncDto {
  newCropFields: ExternalCropFieldDto[];
  changedCropFields: { item1: ExternalCropFieldDto; item2: ExistingCropFieldDto }[];
  deletedCropFields: ExistingCropFieldDto[];
}

export interface SyncCropFieldsRequest {
  cropFieldType: CropFieldType;
  externalCropFieldSource: ExternalCropFieldSources;
  externalId: string;
  selectedNewExternalIds: string[];
  selectedChangedExternalIds: string[];
}

export interface SelectableExternalCropField extends ExternalCropFieldDto {
  isSelected: boolean;
  cropName: string;
  cropVarietyName: string;
}

export interface SelectableExistingCropField extends ExistingCropFieldDto {
  isSelected: boolean;
  cropName: string;
  cropVarietyName: string;
}

export interface SelectableChangedCropField {
  external: SelectableExternalCropField;
  existing: SelectableExistingCropField;
  isSelected: boolean;
}

export interface SyncCropFieldsV2Selection {
  selectedNewExternalIds: string[];
  selectedChangedExternalIds: string[];
  year: number;
}
